
import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, DollarSign, AlertCircle, RefreshCw } from "lucide-react";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useDynamicSlots, TimeSlot } from '@/hooks/useDynamicSlots';

interface DynamicTimeSlotGridProps {
  psychologistId: string;
  selectedDate?: Date;
  selectedDuration: number;
  onSelectTimeSlot: (time: string, duration: number, price: number) => void;
  onDurationChange: (duration: number) => void;
}

const DynamicTimeSlotGrid: React.FC<DynamicTimeSlotGridProps> = ({
  psychologistId,
  selectedDate,
  selectedDuration,
  onSelectTimeSlot,
  onDurationChange
}) => {
  const {
    pricing,
    isLoading,
    loadPsychologistSchedule,
    getGroupedSlots
  } = useDynamicSlots();

  // Carregar dados quando data ou duração muda
  useEffect(() => {
    if (selectedDate && psychologistId) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      console.log('📅 [DynamicTimeSlotGrid] Carregando dados:', { 
        selectedDate: selectedDate.toISOString(),
        dateStr, 
        selectedDuration,
        psychologistId
      });
      
      loadPsychologistSchedule(psychologistId, dateStr);
    }
  }, [selectedDate, psychologistId, selectedDuration, loadPsychologistSchedule]);

  // Gerar slots baseados na duração selecionada
  const groupedSlots = useMemo(() => {
    if (!selectedDate) {
      return {};
    }
    
    console.log('🎯 [DynamicTimeSlotGrid] Gerando slots para duração:', selectedDuration);
    const result = getGroupedSlots(selectedDuration);
    
    console.log('📊 [DynamicTimeSlotGrid] Slots agrupados:', {
      periods: Object.keys(result),
      totalSlots: Object.values(result).flat().length
    });
    
    return result;
  }, [selectedDate, selectedDuration, getGroupedSlots]);

  const availableSlots = useMemo(() => {
    const available = Object.values(groupedSlots).flat().filter(slot => slot.available);
    return available;
  }, [groupedSlots]);

  const totalSlots = useMemo(() => {
    return Object.values(groupedSlots).flat().length;
  }, [groupedSlots]);

  // Tradução dos períodos
  const periodTranslation: Record<string, string> = {
    morning: 'Manhã',
    afternoon: 'Tarde',
    evening: 'Noite'
  };

  const handleSlotClick = (slot: TimeSlot) => {
    console.log('⏰ [DynamicTimeSlotGrid] Slot selecionado:', slot);
    onSelectTimeSlot(slot.time, slot.duration, slot.price);
  };

  const handleRefresh = () => {
    if (selectedDate && psychologistId) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      loadPsychologistSchedule(psychologistId, dateStr);
    }
  };

  if (!selectedDate) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <Clock className="h-5 w-5 mr-2" />
            Selecione uma Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground">
            Escolha uma data para ver os horários disponíveis.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Horários Disponíveis - {format(selectedDate, "dd 'de' MMMM", { locale: ptBR })}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <Badge variant="outline">
            Duração: {selectedDuration} minutos
          </Badge>
          {totalSlots > 0 && (
            <span>
              {availableSlots.length} de {totalSlots} horários disponíveis
            </span>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Skeleton className="h-5 w-24" />
              <div className="grid grid-cols-3 gap-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            </div>
          </div>
        ) : totalSlots > 0 ? (
          availableSlots.length > 0 ? (
            <div className="space-y-6">
              {Object.entries(groupedSlots).map(([period, slots]) => {
                const availablePeriodSlots = slots.filter(slot => slot.available);
                if (availablePeriodSlots.length === 0) return null;
                
                return (
                  <div key={period} className="space-y-3">
                    <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
                      {periodTranslation[period]}
                    </h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                      {availablePeriodSlots.map((slot, index) => (
                        <Button
                          key={index}
                          variant="outline"
                          className="h-14 flex flex-col items-center justify-center gap-1 hover:bg-blue-50 hover:border-blue-300"
                          onClick={() => handleSlotClick(slot)}
                        >
                          <Clock className="h-4 w-4" />
                          <span className="text-sm font-medium">{slot.time}</span>
                          <span className="text-xs text-muted-foreground">
                            {slot.duration}min
                          </span>
                        </Button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-2 text-amber-400" />
              <p className="font-medium">Todos os horários estão ocupados</p>
              <p className="text-sm">Para a duração de {selectedDuration} minutos</p>
              <p className="text-xs mt-2">Tente uma duração diferente ou outra data</p>
            </div>
          )
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p className="font-medium">Nenhum horário configurado</p>
            <p className="text-sm">O psicólogo não configurou horários para esta data</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DynamicTimeSlotGrid;
